import { FC, useEffect, useMemo, useState } from 'react';
import {
  AlgorithmTestCalculationType,
  Detail,
  ErrorAlertWithDrawer,
  formatCodeName,
  formatDate,
  formatDateTimeShort,
  getDatePeriodString,
  ISODate,
  OutputTestCalculationDetailsType,
  OutputTestCalculationType,
  Padding,
  PopupView,
  Spinner,
  TestCalculationExcelUrl,
  TotalEntries,
  useDebounce,
  useMutateTestCalculateDagQuery,
  useMutationErrors,
  useQueryParams,
  useSystemClassifierItemById,
  useTestCalculateOutputDataQuery,
  useTestCalculateQuery,
  VersionDisplay,
  VersionStatus,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Flex } from 'antd';
import styled from 'styled-components';
import translations from './OutputDataTestCalculationReport.translations.json';
import { CalculationErrorsTable } from '../errorReport/CalculationErrorsTable';
import { TestCalculationSegment } from '../types';
import { TestCalculationReportButtonNav } from './TestCalculationReportButtonNav';
import { TestCalculationOutputDataTable } from './TestCalculationOutputDataTable';
import { useOutputDataPagination } from './useOutputDataPagination';
import { FooterPagination } from '../../classifier-mapping/ClassifierMappingDirectory.styles';
import { MissingSourceDataErrorsTable } from '../errorReport/MissingSourceDataErrorsTable';
import * as Icon from 'react-feather';
import { expand } from 'uri-template-lite';

const { addTranslations, t } = i18nNsFactory(
  'output-data-test-calculation-error-report',
);
addTranslations(translations);

export const OutputDataTestCalculationReport: FC = () => {
  const [{ algorithmIds, sourceDataVersionId, valueDate, segment }] =
    useQueryParams();
  const calculationParams: AlgorithmTestCalculationType = useMemo(
    () => ({
      algorithmIds: algorithmIds?.split(',').map((id) => Number(id)) || [],
      sourceDataVersionId: Number(sourceDataVersionId) || 0,
      valueDate: valueDate as ISODate,
    }),
    [algorithmIds, sourceDataVersionId, valueDate],
  );

  const { mutateAsync: testCalculate } = useMutateTestCalculateDagQuery({
    throwOnError: true,
  });
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors();

  const [testCalculationDetails, setTestCalculationDetails] =
    useState<OutputTestCalculationDetailsType | null>(null);

  const {
    data: testCalculation,
    isLoading: isTestCalculating,
    error: testCalculationError,
  } = useTestCalculateQuery(testCalculationDetails?.dagRunId ?? '', {
    enabled: !!testCalculationDetails?.dagRunId,
  });

  // initial bootstrap query to get column definitions
  const { data: outputDataBootstrapPage } = useTestCalculateOutputDataQuery(
    {
      dagRunId: testCalculationDetails?.dagRunId ?? '',
    },
    {
      enabled: !!testCalculation,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const { queryParameters, tableProps, paginationProps, updatePageData } =
    useOutputDataPagination(outputDataBootstrapPage);
  const debouncedParams = useDebounce(queryParameters);
  const { data: outputDataPage } = useTestCalculateOutputDataQuery(
    {
      ...debouncedParams,
      dagRunId: testCalculationDetails?.dagRunId ?? '',
    },
    {
      enabled: !!testCalculation && !!outputDataBootstrapPage,
    },
  );

  useEffect(() => {
    clearMutationErrors();
    const run = async () => {
      try {
        const responseData = await testCalculate({
          data: calculationParams,
        });
        if (!responseData) return;
        setTestCalculationDetails(responseData);
      } catch (error: any) {
        setMutationErrors(error.response?.data.errors ?? []);
      }
    };
    run();
  }, [
    clearMutationErrors,
    calculationParams,
    setMutationErrors,
    testCalculate,
  ]);

  useEffect(() => {
    if (testCalculationError) {
      setMutationErrors(testCalculationError.response?.data.errors ?? []);
    }
  }, [setMutationErrors, testCalculationError]);

  useEffect(() => {
    if (outputDataPage) {
      updatePageData(outputDataPage);
    }
  }, [outputDataPage, updatePageData]);

  const counts = useMemo(
    () => ({
      outputData: outputDataPage?.totalElements || 0,
      calculationErrors: testCalculation
        ? testCalculation.calculationErrors.length
        : 0,
      missingSourceData: testCalculation
        ? testCalculation.missingSourceDataErrors.length
        : 0,
    }),
    [outputDataPage?.totalElements, testCalculation],
  );

  return (
    <PopupView
      isLoading={testCalculationDetails === null}
      title={t('algorithmsTestReport')}
      actions={
        testCalculationDetails && (
          <>
            <VersionDisplay
              versionNumber={testCalculationDetails.versionNumber}
              status={testCalculationDetails.statusCode as VersionStatus}
            >
              {getDatePeriodString(
                testCalculationDetails.validPeriod[0],
                testCalculationDetails.validPeriod[1],
              )}
            </VersionDisplay>
            <Button
              id="download-excel"
              icon={<Icon.Download />}
              loading={isTestCalculating}
              href={expand(TestCalculationExcelUrl, {
                dagRunId: testCalculationDetails.dagRunId,
              })}
              download
            />
          </>
        )
      }
      contentId="output-test-calculation-report-main-content"
      footer={
        testCalculationDetails && (
          <>
            <TotalEntries count={counts[segment as TestCalculationSegment]} />
            {segment === 'outputData' && outputDataPage && (
              <FooterPagination {...paginationProps} />
            )}
          </>
        )
      }
    >
      {testCalculationDetails && (
        <>
          <ErrorAlertWithDrawer
            errorAction={t('calculation')}
            validationMessagesMap={mutationErrors}
          />
          <Padding>
            <TestCalculationDetails
              testCalculationDetails={testCalculationDetails}
              testCalculation={testCalculation}
            />
          </Padding>
          <Padding horizontal>
            {isTestCalculating ? (
              <StyledSpinner message={t('loading')} />
            ) : (
              <Flex vertical gap="large">
                <TestCalculationReportButtonNav
                  counts={counts}
                  params={calculationParams}
                />
                {segment === 'outputData' && outputDataBootstrapPage && (
                  <TestCalculationOutputDataTable
                    initialData={outputDataBootstrapPage.content}
                    data={outputDataPage?.content || []}
                    {...tableProps}
                  />
                )}
                {segment === 'calculationErrors' && (
                  <CalculationErrorsTable
                    data={testCalculation?.calculationErrors ?? []}
                    scrollContainerSelector="#output-test-calculation-report-main-content"
                  />
                )}
                {segment === 'missingSourceData' && (
                  <MissingSourceDataErrorsTable
                    data={testCalculation?.missingSourceDataErrors ?? []}
                    scrollContainerSelector="#output-test-calculation-report-main-content"
                  />
                )}
              </Flex>
            )}
          </Padding>
        </>
      )}
    </PopupView>
  );
};

const TestCalculationDetails = ({
  testCalculationDetails,
  testCalculation,
}: {
  testCalculationDetails: OutputTestCalculationDetailsType;
  testCalculation?: OutputTestCalculationType;
}) => {
  const { algorithmCodes, valueDate, sourceDataVersionId, queueTime } =
    testCalculationDetails;
  const { startTime, endTime } = testCalculation || {};
  const sourceDataVersion = useSystemClassifierItemById(
    'SOURCE_DATA_VERSION',
    sourceDataVersionId,
  )?.name;
  const count = testCalculationDetails.algorithmCodes.length;

  return (
    <>
      <Padding bottom>
        <h4>{formatCodeName(testCalculationDetails)}</h4>
      </Padding>
      <Flex vertical gap="small">
        <Detail label={t('field.algorithms', { count })}>
          {algorithmCodes.join(', ')}
        </Detail>
        <Detail label={t('field.valueDate')}>{formatDate(valueDate)}</Detail>
        <Detail label={t('field.sourceDataVersion')}>
          {sourceDataVersion}
        </Detail>
        <Detail label={t('field.queueTime')}>
          {formatDateTimeShort(queueTime)}
        </Detail>
        {testCalculation && (
          <>
            <Detail label={t('field.startTime')}>
              {startTime ? formatDateTimeShort(startTime) : '-'}
            </Detail>
            <Detail label={t('field.endTime')}>
              {endTime ? formatDateTimeShort(endTime) : '-'}
            </Detail>
          </>
        )}
      </Flex>
    </>
  );
};

const StyledSpinner = styled(Spinner)`
  position: fixed;
  width: 100%;
  height: 100%;
  z-index: 1;
`;
